import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search } from 'lucide-react';
interface PaletteItem {
  id: string;
  label: string;
  action: () => void;
  keywords?: string;
}
interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: string) => void;
  onOpenChat: () => void;
}
export function CommandPalette({
  isOpen,
  onClose,
  onNavigate,
  onOpenChat
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const items: PaletteItem[] = useMemo(() => [{
    id: 'voice',
    label: 'Voice',
    action: () => onNavigate('voice')
  }, {
    id: 'chat',
    label: 'Chat',
    action: () => onNavigate('chat')
  }, {
    id: 'tasks',
    label: 'Tasks',
    action: () => onNavigate('tasks')
  }, {
    id: 'actions',
    label: 'Actions',
    action: () => onNavigate('actions')
  }, {
    id: 'transcripts',
    label: 'Transcripts',
    action: () => onNavigate('transcripts')
  }, {
    id: 'lists',
    label: 'Lists',
    action: () => onNavigate('lists')
  }, {
    id: 'summaries',
    label: 'Summaries',
    action: () => onNavigate('summaries')
  }, {
    id: 'library',
    label: 'Library',
    action: () => onNavigate('library')
  }, {
    id: 'data',
    label: 'Data',
    action: () => onNavigate('data')
  }, {
    id: 'messages',
    label: 'Messages',
    action: () => onNavigate('messages')
  }, {
    id: 'feed',
    label: 'Feed',
    action: () => onNavigate('feed')
  }, {
    id: 'source-rules',
    label: 'Source Rules',
    action: () => onNavigate('source-rules')
  }, {
    id: 'integrations',
    label: 'Integrations',
    action: () => onNavigate('integrations')
  }, {
    id: 'plugins',
    label: 'Plugins',
    action: () => onNavigate('plugins')
  }, {
    id: 'settings',
    label: 'Settings',
    action: () => onNavigate('settings')
  }, {
    id: 'open-chat',
    label: 'Open AI chat',
    action: onOpenChat,
    keywords: 'capture note thought sparkle assistant'
  }], [onNavigate, onOpenChat]);
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(item => item.label.toLowerCase().includes(q) || (item.keywords || '').toLowerCase().includes(q));
  }, [items, query]);
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);
  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, filtered.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[selectedIndex];
        if (item) {
          item.action();
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, filtered, selectedIndex, onClose]);
  return <AnimatePresence data-id="element-670">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} data-id="element-671" />
          <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed top-[15vh] left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50 px-4" data-id="element-672">
            <div className="bg-[#1c1c22] rounded-[16px] overflow-hidden" data-id="element-673">
              <div className="flex items-center gap-3 px-5 py-4" data-id="element-674">
                <Search size={14} className="text-[#8a8680]/40" data-id="element-675" />
                <input type="text" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search" className="flex-1 bg-transparent text-[#e8e4df] outline-none text-[14px] placeholder:text-[#8a8680]/30" data-id="element-676" />
              </div>
              <div className="max-h-[50vh] overflow-y-auto pb-2" data-id="element-677">
                {filtered.length === 0 && <div className="py-8 text-center text-[#8a8680]/40 text-[13px]" data-id="element-678">No matches</div>}
                {filtered.map((item, i) => {
              const isSelected = i === selectedIndex;
              return <button key={item.id} onClick={() => {
                item.action();
                onClose();
              }} onMouseEnter={() => setSelectedIndex(i)} className={`w-full text-left px-5 py-2 text-[14px] transition-colors ${isSelected ? 'text-[#e8e4df] bg-[#252530]/40' : 'text-[#8a8680]'}`} data-id="element-679">
                      {item.label}
                    </button>;
            })}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}